import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Navigate } from 'react-router';

import { useTypedParams } from '@sorare/routing';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK_REWARDS,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import type {
  FixtureCompetitionRewardsPageQuery,
  FixtureCompetitionRewardsPageQueryVariables,
} from './__generated__/index.graphql.ts';

const FIXTURE_COMPETITION_COMPETITION_REWARDS_PAGE_QUERY = gql`
  query FixtureCompetitionRewardsPageQuery($competition: String!) {
    so5 {
      so5LeaderboardGroupInterface(slug: $competition) {
        slug
        so5LeagueTrackGroups {
          slug
          so5LeagueTracks {
            slug
            mySo5LeaderboardContenders {
              slug
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  FixtureCompetitionRewardsPageQuery,
  FixtureCompetitionRewardsPageQueryVariables
>;

export const loaderFn = <
  Params extends {
    competition: string;
  },
>() =>
  queryLoader<Params>(
    FIXTURE_COMPETITION_COMPETITION_REWARDS_PAGE_QUERY,
    ({ competition }) => ({
      competition,
    })
  );

export const FixtureCompetitionRewardsPage = <
  Params extends { fixture: string; competition: string; eventType: string },
>() => {
  const { data } = useQueryLoader<FixtureCompetitionRewardsPageQuery>();
  const { generateSportContextPath } = useSportContext();

  const { fixture, competition, eventType } = useTypedParams<Params>();
  const { so5LeaderboardGroupInterface: so5League } = data.so5 || {};

  const trackGroupSlugWithMostContendersAndLowerRarity = [
    ...(so5League?.so5LeagueTrackGroups || []),
  ]
    .sort((a, b) => {
      const aContenders = a.so5LeagueTracks.reduce(
        (acc, value) => acc + value.mySo5LeaderboardContenders.length,
        0
      );
      const bContenders = b.so5LeagueTracks.reduce(
        (acc, value) => acc + value.mySo5LeaderboardContenders.length,
        0
      );

      return bContenders - aContenders;
    })
    .shift();

  if (!trackGroupSlugWithMostContendersAndLowerRarity) {
    return (
      <Navigate
        replace
        to={generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS, {
          params: { fixture, eventType },
        })}
      />
    );
  }

  return (
    <Navigate
      replace
      to={generateSportContextPath(
        ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK_REWARDS,
        {
          params: {
            fixture,
            eventType,
            competition,
            trckGrp: trackGroupSlugWithMostContendersAndLowerRarity.slug,
            trck: trackGroupSlugWithMostContendersAndLowerRarity
              .so5LeagueTracks[0].slug,
          },
        }
      )}
    />
  );
};
